import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TripSelector from '../TripSelector';
import { TripAssignmentDialog } from '../booking-management/TripAssignmentDialog';
import { ThemedButton, ThemedCard, ThemedBadge } from '../themed';
import { useTheme } from '../../contexts/ThemeContext';

const ASSIGNMENT_STATUSES = Object.freeze([
  'scheduled',
  'dispatched',
  'in_progress',
]);
const MUTABLE_ASSIGNMENT_STATUSES = new Set(['scheduled', 'dispatched']);

const AdminTripAssignmentWorkspace = ({ currentEntity }) => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [dialogTrip, setDialogTrip] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const pendingRefreshRef = useRef(null);
  const refreshGenerationRef = useRef(0);
  const currentEntityIdRef = useRef(currentEntity?.entityId?.toString() || '');

  useEffect(() => {
    currentEntityIdRef.current = currentEntity?.entityId?.toString() || '';
  }, [currentEntity?.entityId]);

  useEffect(() => () => {
    pendingRefreshRef.current?.resolve({ success: false, stale: true });
    pendingRefreshRef.current = null;
  }, []);

  const handleSelectTrip = useCallback((trip) => {
    const entityId = currentEntityIdRef.current;
    if (
      trip
      && String(trip.entity_id ?? trip.entityId) !== entityId
    ) {
      return;
    }
    setSelectedTrip(trip);
    if (!trip) {
      setDialogTrip(null);
    }
  }, []);

  const handleSelectionInvalidated = useCallback(() => {
    setDialogTrip(null);
  }, []);

  const handleListRefresh = useCallback((outcome) => {
    const pending = pendingRefreshRef.current;
    if (
      !pending
      || String(outcome.entityId) !== pending.entityId
      || outcome.refreshKey !== pending.refreshKey
    ) {
      return;
    }
    pendingRefreshRef.current = null;
    if (String(outcome.selectedTripId || '') !== pending.tripId) {
      // A newer selection superseded the refresh requested by the saved
      // dialog snapshot. Settle the dialog callback without treating the
      // unrelated list response as confirmation of the saved trip.
      pending.resolve({ ...outcome, success: false, stale: true });
      return;
    }
    pending.resolve(outcome);
  }, []);

  const refreshAfterSave = useCallback(async () => {
    const entityId = currentEntityIdRef.current;
    const snapshotEntityId = dialogTrip?.__entityId?.toString();
    const tripId = dialogTrip?.id?.toString();
    if (!entityId || snapshotEntityId !== entityId || !tripId) return;
    const requestRefreshKey = refreshGenerationRef.current + 1;
    refreshGenerationRef.current = requestRefreshKey;

    const outcome = await new Promise((resolve) => {
      pendingRefreshRef.current = {
        resolve,
        entityId,
        tripId,
        refreshKey: requestRefreshKey
      };
      setRefreshKey(requestRefreshKey);
    });

    if (outcome.success || outcome.stale || currentEntityIdRef.current !== entityId) {
      return;
    }

    setSelectedTrip(null);
    toast.warn(t('operations.assignmentSavedRefreshFailed'));
  }, [dialogTrip?.__entityId, dialogTrip?.id, t]);

  const canEdit = MUTABLE_ASSIGNMENT_STATUSES.has(selectedTrip?.status);
  const hasExistingAssignment = Boolean(
    selectedTrip
    && (
      selectedTrip.driver_id != null
      || selectedTrip.vehicle_id != null
      || selectedTrip.selected_archived_type
    )
  );
  const entityId = currentEntity?.entityId;

  return (
    <div className="space-y-4">
      <ThemedCard>
        <ThemedCard.Header>
          <div>
            <h2 className={cn('text-xl font-semibold', text.primary)}>
              {t('operations.tripAssignments')}
            </h2>
            <p className={cn('mt-1 text-sm', text.muted)}>
              {t('operations.tripAssignmentsHelp')}
            </p>
          </div>
        </ThemedCard.Header>
        <ThemedCard.Body>
          <TripSelector
            currentEntity={currentEntity}
            selectedTripId={selectedTrip?.id || ''}
            selectedTrip={selectedTrip}
            onSelectTrip={handleSelectTrip}
            onSelectionInvalidated={handleSelectionInvalidated}
            allowHistory={false}
            selectionMode="row"
            projection="assignment"
            includeSeries={false}
            tripStatuses={ASSIGNMENT_STATUSES}
            refreshKey={refreshKey}
            onListRefresh={handleListRefresh}
          />

          {selectedTrip && (
            <div className="mt-4 grid gap-3 rounded-lg border border-gray-200 p-4 text-sm dark:border-gray-700 sm:grid-cols-2">
              <div>
                <span className={text.muted}>{t('driver')}:</span>{' '}
                <span className={text.primary}>{selectedTrip.driver_name || t('notAssigned')}</span>
              </div>
              <div>
                <span className={text.muted}>{t('Vehicle')}:</span>{' '}
                <span className={text.primary}>{selectedTrip.vehicle_name || t('notAssigned')}</span>
              </div>
              <div>
                <span className={text.muted}>{t('operations.routeType')}:</span>{' '}
                <span className={text.primary}>{selectedTrip.selected_archived_type || t('notAssigned')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={text.muted}>{t('status')}:</span>
                <ThemedBadge variant={selectedTrip.status === 'in_progress' ? 'primary' : 'default'}>
                  {t(`tripStatus.${selectedTrip.status}`)}
                </ThemedBadge>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            {selectedTrip?.status === 'in_progress' && (
              <span className={cn('text-sm', text.muted)}>
                {t('operations.assignmentReadOnly')}
              </span>
            )}
            <ThemedButton
              type="button"
              variant="primary"
              disabled={!selectedTrip || !canEdit}
              onClick={() => setDialogTrip({
                ...selectedTrip,
                __entityId: entityId
              })}
            >
              {hasExistingAssignment
                ? t('operations.editAssignment')
                : t('operations.assignTrip')}
            </ThemedButton>
          </div>
        </ThemedCard.Body>
      </ThemedCard>

      <TripAssignmentDialog
        isOpen={Boolean(dialogTrip)}
        onClose={() => setDialogTrip(null)}
        trip={dialogTrip}
        entityId={entityId}
        onSave={refreshAfterSave}
      />
    </div>
  );
};

export default AdminTripAssignmentWorkspace;
